import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { EVENT_STATUS, fmtDate } from "@/lib/format";
import { Confirm, Pill } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { PageTitle, Panel, Stat } from "./AdminLayout";
import { EventForm } from "./EventForm";
import { EventRoutes } from "./EventRoutes";
import { PaidRegistrations } from "./PaidRegistrations";

function StaffAssign({ event, setEvent }) {
  const [keepers, setKeepers] = useState([]);
  const [sel, setSel] = useState(new Set(event.keeper_ids));
  useEffect(() => {
    api.get("/admin/staff").then((r) => setKeepers(r.data.items.filter((u) => u.role === "keeper")));
  }, []);
  const save = async () => {
    try {
      setEvent((await api.put(`/admin/events/${event.id}/staff`, { keeper_ids: [...sel] })).data);
      toast.success("Penugasan keeper disimpan.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2">
      {keepers.map((k) => (
        <label key={k.id} className="flex items-center gap-2 text-sm">
          <Checkbox checked={sel.has(k.id)} data-testid={`assign-keeper-${k.id}`}
            onCheckedChange={(c) => { const s = new Set(sel); c ? s.add(k.id) : s.delete(k.id); setSel(s); }} />
          {k.name} <span className="text-xs text-muted-foreground">{k.email}</span>
        </label>
      ))}
      <Button size="sm" onClick={save} data-testid="save-staff-assign-button">Simpan penugasan</Button>
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  useEffect(() => {
    api.get(`/admin/events/${id}`).then((r) => setE(r.data));
  }, [id]);
  if (!e) return <Skeleton className="h-64 rounded-2xl" />;
  const save = async (payload) => {
    try {
      setE((await api.put(`/admin/events/${id}`, payload)).data);
      toast.success("Event disimpan.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const statusOp = async (op) => {
    try {
      const { data } = await api.post(`/admin/events/${id}/${op}`);
      setE(data);
      if (op === "publish") {
        toast.success("Event dipublikasikan.");
      } else if (op === "reopen") {
        toast.success("Event dibuka kembali dan siap menerima aktivitas baru.");
      } else {
        toast.success(`Event ditutup. ${data.cancelled} tiket menunggu dibatalkan.`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event dihapus.");
      window.location.href = "/admin/events";
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  return (
    <div>
      <Link to="/admin/events" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1A3323]" data-testid="back-to-events"><ArrowLeft className="h-4 w-4" /> Event</Link>
      <PageTitle title={e.name} desc={`${fmtDate(e.date)} · ${e.start_time}–${e.end_time} · ${e.location}`}>
        <div className="flex items-center gap-2">
          <Pill className={EVENT_STATUS[e.status].cls} testid="event-status-pill">{EVENT_STATUS[e.status].label}</Pill>
          {e.status === "DRAFT" && <Button className="rounded-full" onClick={() => statusOp("publish")} data-testid="publish-event-button">Publikasikan</Button>}
          {e.status === "PUBLISHED" && (
            <Confirm testid="close-event" title="Tutup event?" desc="Tiket MENUNGGU dibatalkan. Tiket yang sedang dipanggil/manjat tetap ditangani keeper." destructive confirmText="Tutup event" onConfirm={() => statusOp("close")}
              trigger={<Button variant="outline" className="rounded-full border-red-300 text-red-700" data-testid="close-event-button">Tutup Event</Button>} />
          )}
          {e.status === "CLOSED" && (
            <>
              <Confirm
                testid="reopen-event"
                title="Buka kembali event?"
                desc="Event akan aktif kembali. Tiket menunggu yang sudah dibatalkan tidak dipulihkan."
                confirmText="Reopen event"
                onConfirm={() => statusOp("reopen")}
                trigger={
                  <Button className="rounded-full" data-testid="reopen-event-button">
                    Reopen Event
                  </Button>
                }
              />
              <Confirm
                testid="delete-event"
                title="Hapus event selesai?"
                desc="Data absensi, tiket, dan pengajuan pembayaran event ini akan dihapus."
                destructive
                confirmText="Hapus event"
                onConfirm={remove}
                trigger={
                  <Button
                    variant="outline"
                    className="rounded-full border-red-300 text-red-700"
                    data-testid="delete-event-button"
                  >
                    Hapus Event
                  </Button>
                }
              />
            </>
          )}
        </div>
      </PageTitle>
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat accent label="Check-in" value={`${e.checkin_count}/${e.participant_quota ?? "∞"}`} testid="ed-checkins" />
        <Stat label="Tiket terpakai" value={`${e.tickets_used}/${e.ticket_quota ?? "∞"}`} testid="ed-tickets" />
        <Stat label="Tiket aktif" value={e.active_tickets} testid="ed-active" />
        <Stat label="Jalur" value={e.routes.length} testid="ed-routes" />
      </div>
      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <Panel title="Jalur climbing"><EventRoutes event={e} setEvent={setE} /></Panel>
        <div className="space-y-4">
          <Panel title="Keeper yang ditugaskan"><StaffAssign event={e} setEvent={setE} /></Panel>
          {e.event_type === "PAID" ? (
            <Panel title="Transfer Pembayaran Event">
              <p className="text-sm text-muted-foreground">Informasi rekening transfer diatur pada Detail event.</p>
            </Panel>
          ) : (
            <Panel title="QRIS Kontribusi Kolektif">
              <p className="text-sm text-muted-foreground" data-testid="event-qris-global-note">
                Satu QRIS berlaku untuk semua event. Ubah di menu{" "}
                <Link to="/admin/donation" className="font-semibold text-[#3D6F4B] underline" data-testid="event-qris-global-link">Kontribusi Kolektif QRIS</Link>.
              </p>
            </Panel>
          )}
        </div>
        {e.event_type === "PAID" && <Panel title="Pengajuan pembayaran" className="xl:col-span-2"><PaidRegistrations eventId={e.id} /></Panel>}
        <Panel title="Detail event" className="xl:col-span-2"><EventForm key={e.updated_at || e.id} initial={e} onSubmit={save} /></Panel>
      </div>
    </div>
  );
}
